import { chuanText, keTiShortName } from './daliuren/keti';
/** 解析历史存档的六神煞结果（旧数据兼容：解析失败返回 null） */
export function parseDivinationResult(item) {
    try {
        return JSON.parse(item.result);
    }
    catch {
        return null;
    }
}
/** 解析历史存档的大六壬结果（解析失败返回 null） */
export function parseDaLiuRenResult(item) {
    try {
        const r = JSON.parse(item.result);
        // 结构校验：旧的小六壬结果没有 chuan / keTi 字段
        return r && Array.isArray(r.chuan) && r.keTi ? r : null;
    }
    catch {
        return null;
    }
}
/** 判定历史记录所属体系（兼容无 system 列的旧数据） */
export function historySystem(item) {
    if (item.system === 'daliuren')
        return 'daliuren';
    if (item.mode === 'datetime')
        return 'daliuren';
    return 'xiaoliuren';
}
/**
 * 「所问之事」是否只是随机取数用的抽字（并非用户真问之事）。
 *
 * 随机取数模式下若用户未填问事、也未输入取数汉字，系统会随机抽字并回填到「所问之事」，
 * 使记录可追溯；此时 input.text 与 question 完全相同。凡命中者，转帖标题回退为简约标题、
 * 正文改写为「取数用字」表述，避免把随机字当作问事。
 */
export function isRandomStrokeQuestion(item) {
    const q = item.question?.trim();
    if (!q)
        return false;
    try {
        const input = JSON.parse(item.input);
        return typeof input.text === 'string' && input.text.trim() === q;
    }
    catch {
        return false;
    }
}
/** 六神煞历史 → 草稿 */
function buildXiaoLiuRenDraft(item, question) {
    const parsed = parseDivinationResult(item);
    // 随机抽字回填的「所问之事」不算真问事：标题仍用简约标题
    const isRandomChars = isRandomStrokeQuestion(item);
    const realQuestion = isRandomChars ? '' : question;
    // 简约标题：有真问事时「问事｜卦象」，否则只用卦象
    const brief = `六神煞 · ${parsed?.final.name ?? '占卜'}${parsed ? `（${parsed.final.fortune}）` : ''}`;
    const title = realQuestion ? `${realQuestion.slice(0, 30)}｜${brief}` : brief;
    const modeText = item.mode === 'calendar' ? '日历取数' : '随机取数';
    const stepsText = parsed?.steps.map((s) => s.desc).join('\n') ?? '';
    const questionLine = isRandomChars
        ? `**所问之事**：未记载（本次为随机抽字取数，用字「${question}」）`
        : `**所问之事**：${realQuestion || '未记录所问之事'}`;
    // 结论置顶：所问之事 / 结论 / 推演过程 / 来源
    const conclusion = parsed
        ? `> **结论**：**${parsed.final.name}**（${parsed.final.fortune}）— ${parsed.final.summary}\n>\n> ${parsed.final.desc}`
        : '> **结论**：历史记录解析失败，无推演详情';
    const content = [
        conclusion,
        '',
        questionLine,
        '',
        stepsText ? `**推演**\n\n${stepsText}` : '',
        '',
        `> 由占卜历史转入 · ${modeText}${item.lunar_text ? ` · ${item.lunar_text}` : ''}`,
    ]
        .filter((line) => line !== undefined)
        .join('\n')
        .replace(/\n{3,}/g, '\n\n');
    return { title, content };
}
/** 大六壬历史 → 草稿 */
function buildDaLiuRenDraft(item, question) {
    const r = parseDaLiuRenResult(item);
    const brief = r
        ? `大六壬 · ${keTiShortName(r.keTi)} · 三传${chuanText(r.chuan)}（${r.interpretation.fortune}）`
        : '大六壬 · 占卜';
    const title = question ? `${question.slice(0, 24)}｜${brief}` : brief;
    if (!r) {
        return {
            title,
            content: [
                `**所问之事**：${question || '未记录所问之事'}`,
                '',
                '（历史记录解析失败，无盘面详情）',
                '',
                `> 由占卜历史转入 · 大六壬 · ${item.lunar_text ?? ''}`,
            ].join('\n'),
        };
    }
    const chuanLine = r.chuan
        .map((c) => `${c.position}**${c.zhi}**（${c.tianJiang}·${c.liuQin}${c.kong ? '·空' : ''}）`)
        .join('　');
    const keLine = r.ke
        .map((k) => `${k.name} ${k.xia}／${k.shang}（${k.tianJiang}·${k.liuQin}${k.kong ? '·空' : ''}）`)
        .join('\n');
    const jiLine = r.chuan.map((c) => `${c.position}${c.zhi}遁${c.dunGan ?? '—'}`).join('　');
    const panLine = r.tianJiang.map((t) => `${t.gong}宫${t.zhi}乘${t.jiang}`).join('　');
    const shaLine = r.shenSha
        .filter((s) => s.inChuan || s.inKe)
        .slice(0, 8)
        .map((s) => `${s.name}(${s.value})`)
        .join('、');
    const points = r.interpretation.points
        .slice(0, 6)
        .map((p) => `- 【${p.level}】${p.text}`)
        .join('\n');
    // 结论置顶：结论 / 应期 / 本命行年 / 所问之事 / 盘面明细 / 断课要点
    const content = [
        `> **结论**：${r.interpretation.fortune}｜${r.keTi.name}｜三传${chuanText(r.chuan)}\n>\n> ${r.interpretation.overall}`,
        '',
        `**应期**：${r.yingQi.text}`,
        ...(r.benMing ? [``, `**本命行年**：${r.benMing.text}`] : []),
        '',
        `**所问之事**：${question || '未记录所问之事'}`,
        '',
        `**四柱月将**：${r.siZhuText}　月将${r.yueJiang.zhi}（${r.yueJiang.name}，${r.yueJiang.qi}后换将）　占时${r.zhanShi}`,
        `**天地盘**：${r.pan.layout}　贵人临${r.guiRenGong}宫（${r.jiangDirection}布，${r.dayNight}占）`,
        `**旬空**：${r.xun}，空亡${r.kongWang.join('')}`,
        '',
        `**课体**：${r.keTi.name}${r.keTi.extras.length > 0 ? `　（${r.keTi.extras.join('、')}）` : ''}`,
        '',
        `**四课**\n\n${keLine}`,
        '',
        `**三传**\n\n${chuanLine}`,
        '',
        `**遁干**：${jiLine}`,
        '',
        `**天将布宫**：${panLine}`,
        '',
        `**神煞**：${shaLine || '无明显神煞入传'}`,
        '',
        `**断课要点（${r.interpretation.fortune}）**`,
        '',
        points,
        '',
        `> 由占卜历史转入 · 大六壬 · 时间起课${item.lunar_text ? ` · ${item.lunar_text}` : ''}`,
    ]
        .filter((line) => line !== undefined)
        .join('\n')
        .replace(/\n{3,}/g, '\n\n');
    return { title, content };
}
/** 历史记录 → 占卜帖草稿（按 system 分派） */
export function buildDivinationPostDraft(item) {
    const question = item.question?.trim() ?? '';
    return historySystem(item) === 'daliuren'
        ? buildDaLiuRenDraft(item, question)
        : buildXiaoLiuRenDraft(item, question);
}
/**
 * 历史记录「所问之事」展示文案（列表/卡片共用）：
 * 随机抽字回填的标注为「随机取字 XXX」，其余回退到农历描述或「未记录所问之事」。
 */
export function historyQuestionLabel(item) {
    if (item.question) {
        return isRandomStrokeQuestion(item) ? `随机取字 ${item.question}` : item.question;
    }
    return item.lunar_text || '未记录所问之事';
}
/** 历史列表摘要（小六壬：六神煞 · 掌诀名（吉凶）；大六壬：大六壬 · 课体名 · 三传（吉凶）） */
export function historySummary(item) {
    if (historySystem(item) === 'daliuren') {
        const r = parseDaLiuRenResult(item);
        return r
            ? `大六壬 · ${keTiShortName(r.keTi)} · 三传${chuanText(r.chuan)}（${r.interpretation.fortune}）`
            : '大六壬';
    }
    const p = parseDivinationResult(item);
    return p ? `六神煞 · ${p.final.name}（${p.final.fortune}）` : '六神煞';
}
//# sourceMappingURL=post.js.map